import { supabase } from '@/lib/supabase';
import {
  arrangementOn,
  loadDriverTypeRules,
  loadPayArrangements,
  type DriverTypeRule,
  type PayArrangement,
} from '@/lib/payArrangements';
import type { SummaryKind } from '@/lib/summaryKind';

/**
 * Central, database-driven resolver for driver-payable commission % per payment type.
 *
 * Resolution priority for the effective driver-payable % of a payment type:
 *   0. Leased on that date   (payment_types.leased_percent — only when a dated pay
 *                             arrangement puts the driver on a leased driver type that day)
 *   1. Per-driver override   (driver_payment_type_percentages.payable_percent)
 *   2. Company default        (payment_types.base_commission_percent — the "Standard %" set in Settings)
 *   3. Flat fallback          (adminSettings.commissionPercent passed in)
 *
 * Nothing is hard-coded here — every rate comes from the database settings.
 */

export interface PaymentTypeRate {
  id: string;
  name: string;
  base_commission_percent: number | null;
  contractor_percent: number | null;
  leased_percent: number | null;
  driver_payable_percent: number | null;
  applies_to_driver_commission: boolean | null;
  is_per_unit: boolean | null;
  per_unit_amount: number | null;
}

export interface ResolvedRateContext {
  paymentTypes: PaymentTypeRate[];
  /** driver_name -> (payment_type_id -> payable_percent) */
  overrides: Record<string, Record<string, number>>;
  /** lowercased payment type name -> PaymentTypeRate */
  byName: Record<string, PaymentTypeRate>;
  /** driver_name -> dated pay arrangements, oldest first (only drivers who have a history). */
  arrangements?: Record<string, PayArrangement[]>;
  /** driver_type_id -> its rules (leased? bonus tiers?). */
  driverTypes?: Record<string, DriverTypeRule>;
}

const norm = (s: string | null | undefined) => (s || '').toString().trim().toLowerCase();

/**
 * Single source of truth for the *last-resort* company default numbers, used
 * only when the database / Settings tab has not provided a value yet.
 *
 * These are deliberately kept here (one place) instead of being scattered as
 * magic numbers (e.g. `|| 50`, `|| 15`, `|| 11.5`) across the payout report,
 * driver summary modal and payment modal — which previously disagreed with
 * each other (some used 50, the company default was 60). At runtime the saved
 * Settings values always win; these only apply if Settings is empty.
 */
export const SETTINGS_FALLBACKS = {
  commissionPercent: 60,
  gstPercent: 15,
  insuranceExcess: 15,
  tmHoist: 11.5,
} as const;

interface CompanyAdminSettings {
  commissionPercent?: number | null;
  gstPercent?: number | null;
  insuranceExcess?: number | null;
  tmHoist?: number | null;
}

const pick = (value: number | null | undefined, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) && value !== null && value !== undefined ? n : fallback;
};

/** Flat company commission % (Settings → fallback). */
export const flatCommissionPercent = (s?: CompanyAdminSettings | null): number =>
  pick(s?.commissionPercent, SETTINGS_FALLBACKS.commissionPercent);

/** GST rate as a fraction, e.g. 0.15 (Settings → fallback). */
export const gstRateFraction = (s?: CompanyAdminSettings | null): number =>
  pick(s?.gstPercent, SETTINGS_FALLBACKS.gstPercent) / 100;

/** Weekly insurance excess deduction (Settings → fallback). */
export const insuranceExcessAmount = (s?: CompanyAdminSettings | null): number =>
  pick(s?.insuranceExcess, SETTINGS_FALLBACKS.insuranceExcess);


export const loadPaymentTypeRates = async (
  tenantId?: string | null
): Promise<PaymentTypeRate[]> => {
  let q = supabase
    .from('payment_types')
    .select(
      'id, name, base_commission_percent, contractor_percent, leased_percent, driver_payable_percent, applies_to_driver_commission, is_per_unit, per_unit_amount'
    )
    .order('name');
  // Include this tenant's payment types plus any global (null-tenant) types
  // such as "TM Hoist" so the payout report and driver modal stay in sync.
  if (tenantId) q = q.or(`tenant_id.eq.${tenantId},tenant_id.is.null`);

  const { data, error } = await q;
  if (error) {
    console.error('loadPaymentTypeRates error:', error);
    return [];
  }
  return (data as PaymentTypeRate[]) || [];
};

export const loadDriverOverrides = async (
  driverNames: string[]
): Promise<Record<string, Record<string, number>>> => {
  const map: Record<string, Record<string, number>> = {};
  if (!driverNames.length) return map;
  const { data, error } = await supabase
    .from('driver_payment_type_percentages')
    .select('driver_name, payment_type_id, payable_percent')
    .in('driver_name', driverNames);
  if (error) {
    console.error('loadDriverOverrides error:', error);
    return map;
  }
  (data || []).forEach((r: any) => {
    if (!map[r.driver_name]) map[r.driver_name] = {};
    map[r.driver_name][r.payment_type_id] = Number(r.payable_percent);
  });
  return map;
};

export const buildRateContext = async (
  driverNames: string[],
  tenantId?: string | null
): Promise<ResolvedRateContext> => {
  const [paymentTypes, overrides, arrangements, driverTypes] = await Promise.all([
    loadPaymentTypeRates(tenantId),
    loadDriverOverrides(driverNames),
    loadPayArrangements(driverNames),
    loadDriverTypeRules(),
  ]);
  const byName: Record<string, PaymentTypeRate> = {};
  paymentTypes.forEach((p) => {
    byName[norm(p.name)] = p;
  });
  return { paymentTypes, overrides, byName, arrangements, driverTypes };
};

/** The driver type rules in force for a driver on a given ISO date, or null if they have no dated history. */
export const driverTypeOn = (
  ctx: ResolvedRateContext | null | undefined,
  driverName: string,
  date: string | null | undefined
): DriverTypeRule | null => {
  const arr = arrangementOn(ctx?.arrangements?.[driverName], date);
  return arr ? ctx?.driverTypes?.[arr.driver_type_id] ?? null : null;
};

/** True when a dated arrangement has the driver on a leased driver type on that date. */
export const isLeasedOn = (ctx: ResolvedRateContext | null | undefined, driverName: string, date: string | null | undefined): boolean =>
  !!driverTypeOn(ctx, driverName, date)?.is_leased;

/**
 * Whether turnover on this date counts towards the driver's bonus tier. Only a dated
 * arrangement whose driver type has bonus tiers switched off excludes a day -- drivers with
 * no history are unaffected.
 */
export const bonusEligibleOn = (ctx: ResolvedRateContext | null | undefined, driverName: string, date: string | null | undefined): boolean =>
  driverTypeOn(ctx, driverName, date)?.uses_bonus_tiers !== false;

/**
 * Company-default driver-payable % for a payment type (no driver override applied).
 * base_commission_percent (Settings "Standard %") wins; otherwise the flat fallback.
 */
export const companyDefaultPercent = (
  pt: PaymentTypeRate | undefined,
  flatFallback: number | null
): number | null => {
  if (pt && pt.base_commission_percent !== null && pt.base_commission_percent !== undefined) {
    return Number(pt.base_commission_percent);
  }
  return flatFallback;
};

/**
 * Effective driver-payable % for a given driver + payment type.
 */
export const effectivePercent = (
  ctx: ResolvedRateContext,
  driverName: string,
  paymentTypeName: string,
  flatFallback: number | null,
  /** ISO date of the job. Leave out for the old behaviour (ignores dated arrangements). */
  onDate?: string | null
): number | null => {
  const pt = ctx.byName[norm(paymentTypeName)];
  // 0. Leased on this date: the payment type's Leased %. A per-driver override set while on
  // commission does not follow them onto a lease; with no Leased % set it falls back to the
  // company default.
  if (onDate && isLeasedOn(ctx, driverName, onDate)) {
    if (pt && pt.leased_percent !== null && pt.leased_percent !== undefined) return Number(pt.leased_percent);
    return companyDefaultPercent(pt, flatFallback);
  }
  // 1. Per-driver override
  if (pt) {
    const driverOverrides = ctx.overrides[driverName];
    if (driverOverrides && driverOverrides[pt.id] !== undefined) {
      return Number(driverOverrides[pt.id]);
    }
  }
  // 2. Company default / 3. flat fallback
  return companyDefaultPercent(pt, flatFallback);
};

export interface KindTotals {
  /** Everything collected on days of this kind, all columns. */
  grossTurnover: number;
  /** What the pricing is worked on: ex-GST turnover on commission days; on lease days only the columns that count (Leased % above 0), GST included. */
  netTake: number;
  driverPortion: number;
}

export interface DriverPortionResult {
  /** The same totals split by whether the day was a lease day or a commission day. */
  byKind: { commission: KindTotals; lease: KindTotals };
  grossTurnover: number;
  exGst: number;
  /** GST taken off: commission days only. A leaseholder is paid GST-inclusive, as on the contract payment summary. */
  gstAmount: number;
  /** Commission days' ex-GST turnover plus the counted (Leased % above 0) lease-day takings, GST included. */
  netTake: number;
  /** What the driver keeps, each job priced under the arrangement in force on its date. */
  driverPortion: number;
  /** Gross turnover that counts towards the bonus tier (days on a type with bonus tiers switched off are left out). */
  bonusEligibleGross: number;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

/**
 * One driver's week, priced per day so a mid-week change of arrangement (commission -> lease)
 * is honoured. `byDate` is turnover by pu_date then by source column; days with no usable date
 * (undated jobs) are priced as of `fallbackDate`. For a driver with no dated history this adds
 * up to exactly what a whole-week calculation gives.
 */
export function computeDriverPortion(args: {
  ctx: ResolvedRateContext | null;
  driverName: string;
  byDate: Record<string, Record<string, number>>;
  ptNameFor: (column: string) => string | null | undefined;
  gstRate: number;
  flatCommission: number;
  fallbackDate: string;
}): DriverPortionResult {
  const { ctx, driverName, byDate, ptNameFor, gstRate, flatCommission, fallbackDate } = args;
  let grossTurnover = 0;
  let driverPortion = 0;
  let bonusEligibleGross = 0;
  const byKind = {
    commission: { grossTurnover: 0, netTake: 0, driverPortion: 0 },
    lease: { grossTurnover: 0, netTake: 0, driverPortion: 0 },
  };

  for (const [rawDate, cols] of Object.entries(byDate)) {
    const date = ISO_DAY.test(rawDate) ? rawDate.slice(0, 10) : fallbackDate;
    const bonusOk = bonusEligibleOn(ctx, driverName, date);
    const leased = isLeasedOn(ctx, driverName, date);
    const kindTotals = leased ? byKind.lease : byKind.commission;
    for (const [col, amount] of Object.entries(cols)) {
      const colGross = Number(amount || 0);
      if (!colGross) continue;
      grossTurnover += colGross;
      if (bonusOk) bonusEligibleGross += colGross;
      const ptName = ptNameFor(col);
      let pct: number | null = flatCommission;
      if (ctx && ptName) pct = effectivePercent(ctx, driverName, ptName, flatCommission, date);
      const rate = (pct ?? flatCommission) / 100;
      // A leaseholder is paid on what was collected, GST included (their contract payment
      // summary has no GST line), and a column at 0% (e.g. cash they keep themselves) isn't counted.
      // Commission days work on the ex-GST turnover as always.
      const base = leased ? colGross : colGross / (1 + gstRate);
      const share = base * rate;
      driverPortion += share;
      kindTotals.grossTurnover += colGross;
      if (!leased || rate > 0) kindTotals.netTake += base;
      kindTotals.driverPortion += share;
    }
  }

  const gstAmount = byKind.commission.grossTurnover - byKind.commission.netTake;
  return {
    byKind,
    grossTurnover,
    exGst: grossTurnover / (1 + gstRate),
    gstAmount,
    netTake: byKind.commission.netTake + byKind.lease.netTake,
    driverPortion,
    bonusEligibleGross,
  };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const shortDay = (iso: string) => `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;

/**
 * "Commission 28 Sep - 30 Sep, Leased 1 Oct - 4 Oct" when the driver changed arrangement
 * part-way through the week; null when they were on one arrangement (or have no history).
 */
export function describeWeekArrangements(
  ctx: ResolvedRateContext | null | undefined,
  driverName: string,
  weekStart: string,
  weekEnd: string
): string | null {
  const list = ctx?.arrangements?.[driverName];
  if (!list || list.length === 0) return null;
  const runs: { id: string; name: string; from: string; to: string }[] = [];
  const startMs = Date.parse(`${weekStart.slice(0, 10)}T00:00:00Z`);
  const endMs = Date.parse(`${weekEnd.slice(0, 10)}T00:00:00Z`);
  for (let ms = startMs; ms <= endMs; ms += 86_400_000) {
    const day = new Date(ms).toISOString().slice(0, 10);
    const arr = arrangementOn(list, day);
    if (!arr) continue;
    const last = runs[runs.length - 1];
    if (last && last.id === arr.id) last.to = day;
    else runs.push({ id: arr.id, name: ctx?.driverTypes?.[arr.driver_type_id]?.type_name ?? "Other", from: day, to: day });
  }
  if (runs.length < 2) return null;
  return runs.map((r) => `${r.name} ${r.from === r.to ? shortDay(r.from) : `${shortDay(r.from)} - ${shortDay(r.to)}`}`).join(", ");
}

/**
 * Which summary this driver's week gets: all lease days -> lease, no lease days -> commission,
 * both -> the combined changeover summary. Drivers with no dated history are always commission
 * (their summary is unchanged).
 */
export function weekSummaryKind(
  ctx: ResolvedRateContext | null | undefined,
  driverName: string,
  weekStart: string,
  weekEnd: string
): SummaryKind {
  if (!ctx?.arrangements?.[driverName]?.length) return "commission";
  const startMs = Date.parse(`${weekStart.slice(0, 10)}T00:00:00Z`);
  const endMs = Date.parse(`${weekEnd.slice(0, 10)}T00:00:00Z`);
  let lease = 0;
  let total = 0;
  for (let ms = startMs; ms <= endMs; ms += 86_400_000) {
    total += 1;
    if (isLeasedOn(ctx, driverName, new Date(ms).toISOString().slice(0, 10))) lease += 1;
  }
  if (lease === 0) return "commission";
  return lease === total ? "lease" : "mixed";
}
